// Mock adapter: deterministic, dependency-free, and independent of @ssm/client
// so the layer above can be built and tested before the two halves meet.

import type { PmsError } from '../../errors';
import type {
  EnsureTenantInput,
  EnsureTenantOutput,
  GetUnitInput,
  GetUnitOutput,
  MoveInInput,
  MoveInOutput,
  PmsAdapter,
  PmsOp,
  PmsOpResult,
  PmsRequestEnvelope,
  RecordPaymentInput,
  RecordPaymentOutput,
} from '../../types';

const CAPABILITIES: ReadonlySet<PmsOp> = new Set<PmsOp>([
  'ensureTenant',
  'moveIn',
  'recordPayment',
  'getUnit',
]);

export interface MockAdapterOptions {
  /** Force a given operation to fail, for exercising the runner's decisions. */
  failOn?: Partial<Record<PmsOp, PmsError>>;
}

/** Small stable hash so ids are readable and repeatable across runs. */
function stableId(prefix: string, seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) % 100000;
  }
  return `${prefix}-${String(hash).padStart(5, '0')}`;
}

function envelope(op: PmsOp, body: unknown): PmsRequestEnvelope {
  return { op, vendorOp: `Mock${op}`, path: `/mock/${op}`, body };
}

function respond<T>(op: PmsOp, input: unknown, data: T, failOn?: PmsError): PmsOpResult<T> {
  const request = envelope(op, input);
  if (failOn) return { ok: false, request, error: failOn };
  return { ok: true, request, data };
}

export function createMockAdapter(options: MockAdapterOptions = {}): PmsAdapter {
  const failOn = options.failOn || {};

  return {
    provider: 'mock',
    capabilities: CAPABILITIES,

    ensureTenant(input: EnsureTenantInput) {
      return Promise.resolve(
        respond<EnsureTenantOutput>(
          'ensureTenant',
          input,
          { externalTenantID: stableId('MOCK-T', input.binsTenantID), accessCode: null },
          failOn.ensureTenant,
        ),
      );
    },

    moveIn(input: MoveInInput) {
      return Promise.resolve(
        respond<MoveInOutput>(
          'moveIn',
          input,
          {
            externalLeaseID: stableId('MOCK-L', `${input.binsTenantID}:${input.binsUnitID}`),
            receiptNumber: stableId('MOCK-R', input.binsUnitID),
            accessCode: input.accessCode || null,
          },
          failOn.moveIn,
        ),
      );
    },

    recordPayment(input: RecordPaymentInput) {
      return Promise.resolve(
        respond<RecordPaymentOutput>(
          'recordPayment',
          input,
          { receiptNumber: stableId('MOCK-R', input.binsLedgerEntryID) },
          failOn.recordPayment,
        ),
      );
    },

    getUnit(input: GetUnitInput) {
      return Promise.resolve(
        respond<GetUnitOutput>(
          'getUnit',
          input,
          { vacant: true, externalLeaseID: null },
          failOn.getUnit,
        ),
      );
    },
  };
}
